import React, { useState } from 'react';
import { db } from '../firebaseConfig';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';

function EditSessionModal({ session, onClose }) {
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initial State derived from session
    const [formData, setFormData] = useState({
        availableUntil: session.availableUntil ? session.availableUntil.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '',
        scheduledStartTime: session.scheduledStartTime ? new Date(session.scheduledStartTime.toDate().getTime() - (session.scheduledStartTime.toDate().getTimezoneOffset() * 60000)).toISOString().slice(0, 16) : '',
        note: session.note || '',
        hasChessSet: session.hasChessSet || 'yes',
        isKidFriendly: session.isKidFriendly || false,
        isBeginnerFriendly: session.isBeginnerFriendly || false,
        gameType: session.gameType || 'casual',
        skillLevel: session.skillLevel || 'any'
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const updates = {
                note: formData.note.trim(),
                hasChessSet: formData.hasChessSet,
                isKidFriendly: formData.isKidFriendly,
                isBeginnerFriendly: formData.isBeginnerFriendly,
                gameType: formData.gameType,
                skillLevel: formData.skillLevel
            };

            // Handle Time Updates
            if (session.type === 'now' && formData.availableUntil) {
                const [hours, minutes] = formData.availableUntil.split(':').map(Number);
                const until = new Date();
                until.setHours(hours, minutes, 0, 0);

                // Handle next day wrap
                if (until < new Date()) {
                    until.setDate(until.getDate() + 1);
                }
                updates.availableUntil = Timestamp.fromDate(until);
            } else if (session.type === 'scheduled' && formData.scheduledStartTime) {
                updates.scheduledStartTime = Timestamp.fromDate(new Date(formData.scheduledStartTime));
            }

            await updateDoc(doc(db, 'sessions', session.id), updates);
            toast.success("Session updated!");
            onClose();
        } catch (error) {
            console.error("Error updating session:", error);
            toast.error("Failed to update session.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[1300] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="bg-indigo-600 p-4 flex justify-between items-center text-white">
                    <h3 className="font-bold text-lg">Edit Session</h3>
                    <button onClick={onClose} className="hover:bg-white/20 rounded-full p-1">✕</button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Time Fields */}
                    {session.type === 'now' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Available Until</label>
                            <input
                                type="time"
                                value={formData.availableUntil}
                                onChange={e => setFormData({ ...formData, availableUntil: e.target.value })}
                                className="w-full border rounded p-2"
                                required
                            />
                        </div>
                    )}

                    {session.type === 'scheduled' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time</label>
                            <input
                                type="datetime-local"
                                value={formData.scheduledStartTime}
                                onChange={e => setFormData({ ...formData, scheduledStartTime: e.target.value })}
                                className="w-full border rounded p-2"
                                required
                            />
                        </div>
                    )}

                    {/* Meta Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Skill Level</label>
                            <select value={formData.skillLevel} onChange={e => setFormData({ ...formData, skillLevel: e.target.value })} className="w-full border rounded p-2">
                                <option value="any">Any</option>
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Game Type</label>
                            <select value={formData.gameType} onChange={e => setFormData({ ...formData, gameType: e.target.value })} className="w-full border rounded p-2">
                                <option value="casual">Casual</option>
                                <option value="blitz">Blitz</option>
                                <option value="rapid">Rapid</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Chess Set</label>
                        <select value={formData.hasChessSet} onChange={e => setFormData({ ...formData, hasChessSet: e.target.value })} className="w-full border rounded p-2">
                            <option value="yes">I have a set</option>
                            <option value="no">I don't have a set</option>
                        </select>
                    </div>

                    <textarea
                        value={formData.note}
                        onChange={e => setFormData({ ...formData, note: e.target.value })}
                        className="w-full border rounded p-2"
                        placeholder="Notes..."
                        rows={3}
                    />

                    <div className="flex gap-4 pt-2">
                        <div className="flex items-center">
                            <input type="checkbox" checked={formData.isKidFriendly} onChange={e => setFormData({ ...formData, isKidFriendly: e.target.checked })} id="edit-kid" className="mr-2" />
                            <label htmlFor="edit-kid" className="text-sm">Kid Friendly</label>
                        </div>
                        <div className="flex items-center">
                            <input type="checkbox" checked={formData.isBeginnerFriendly} onChange={e => setFormData({ ...formData, isBeginnerFriendly: e.target.checked })} id="edit-beg" className="mr-2" />
                            <label htmlFor="edit-beg" className="text-sm">Beginner Friendly</label>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button type="button" onClick={onClose} className="flex-1 py-2 border rounded hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="flex-1 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditSessionModal;
